import * as React from "react";
import { styled, useTheme } from "@mui/system";

import { Button as VaporButton } from "./VaporButton";
import { ButtonProps } from "@vapor/react-material/Button";
// import Typography from "@vapor/react-extended/ExtendedTypography";
import Typography from "@vapor/react-material/Typography";
import Stack from "@vapor/react-material/Stack";

import IconButton from "@vapor/react-material/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons/faTrashAlt";

interface UploadedFile {
  size: any;
  name: string;
  encoding: string;
  mimetype: string;
  data: Buffer;
  truncated: boolean;
  mv(path: string, callback: (err: any) => void): void;
  mv(path: string): Promise<void>;
}

export type VaporUploaderProps = {
  children?: React.ReactNode;
  /**
   * Function when you upload files
   */
  onFileUpload: Function;
  /**
   * If `true` it will show the draggable version of uploader
   * @default false
   */
  draggable?: boolean;
  /**
   * Text for drag part of component
   */
  draggableText?: string;
  /**
   * Text will be displayd when files are in drag area and will be dropped
   */
  dropText?: string;
  /**
   * Main label shown on top of upload button
   */
  label?: string;
  /**
   * In cases you want to add more information to the uploader component
   */
  description?: string;
  /**
   * Button text
   */
  buttonLabel: string;
  /**
   * If `true` it will make drag borders dislayed with a red error color.
   * @default false
   */
  error?: boolean;
  /**
   * In cases you want to display an error message
   */
  errorMessage?: string;
  /**
   * Other props you want to add to file input
   */
  inputFileProps?: object;
  /**
   * The button variant to use
   * @default text
   */
  buttonVariant?: "contained" | "outlined" | "text";
  /**
   * If you use this prop it will display the files in a default version of files list.
   * In cases you have completely different use case, you can avoid using `files` and `renderItem`.
   */
  files?: UploadedFile[];
  /**
   * If you want to display files list differently use this prop
   */
  renderItem?: (file: any, fileIndex: any) => React.ReactNode;
  /**
   * Changes the height of the draggable area
   */
  height?: string | number;
  /**
   * A callback function, will be executed when removing file button is clicked
   */
  onRemove?: Function;
};

export type VaporButtonProps = {
  component?: React.ElementType;
} & ButtonProps;

function Button({ children, ...rest }: VaporButtonProps) {
  return <VaporButton {...rest}>{children}</VaporButton>;
}

let count = 0;

export const VaporUploader = styled(
  (props: VaporUploaderProps) => {
    const {
      onFileUpload,
      label,
      description,
      buttonLabel,
      buttonVariant,
      draggable,
      inputFileProps,
      draggableText,
      dropText,
      error,
      errorMessage,
      files = [],
      renderItem,
      height,
      onRemove = (f) => f,
      ...otherProps
    } = props;

    // const removeFile = (fileIndex) => {

    //   onFileUpload(files.filter((_, i) => i !== fileIndex));
    // };

    const [dragActive, setDragActive] = React.useState(false);
    // ref
    const inputRef = React.useRef(null);

    // handle drag events
    const handleDrag = function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    };
    // triggers when file is dropped
    const handleDrop = function (e) {
      e.preventDefault();
      e.stopPropagation();

      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        onFileUpload([...e.dataTransfer.files]);
        // handleFile(e.dataTransfer.files);
      }
    };
    // triggers when file is selected with click
    const handleChange = function (e) {
      e.preventDefault();
      if (e.target.files && e.target.files[0]) {
        // handleFile(e.target.files);

        onFileUpload([...e.target.files]);
      }
    };

    // triggers the input when the button is clicked
    const onButtonClick = () => {
      inputRef.current.click();
    };

    const theme = useTheme();

    return (
      <Stack direction="column" spacing={1}>
        <Typography variant="boldLabel">{label && label}</Typography>
        <Typography variant="body" color="primary.textSubduedColor">
          {description && description}
        </Typography>
        {draggable === undefined ? (
          <label htmlFor="contained-button-file">
            <input
              ref={inputRef}
              type="file"
              id="contained-button-file"
              style={{ display: "none" }}
              onChange={handleChange}
              {...inputFileProps}
            />
            <Button variant={buttonVariant} component="div" sx={{ width: 120 }}>
              {buttonLabel}
            </Button>
          </label>
        ) : (
          <form
            id="form-file-upload"
            onDragEnter={handleDrag}
            onSubmit={(e) => e.preventDefault()}
            style={{
              border: error
                ? `1px dashed ${theme.palette.primary.error}`
                : dragActive
                ? `1px dashed ${theme.palette.primary.textTitleColor}`
                : `1px dashed ${theme.palette.primary.interactiveDefault}`,
              borderRadius: 2,
              height: height === undefined ? 72 : height
            }}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              id="input-file-upload"
              onChange={handleChange}
              style={{ display: "none" }}
              {...inputFileProps}
            />
            <label
              id="label-file-upload"
              htmlFor="input-file-upload"
              className={dragActive ? "drag-active" : ""}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                  height: "100%",
                  cursor: dragActive ? "grabbing" : "grab"
                }}
              >
                {dragActive ? (
                  <Typography
                    variant="bodyLarge500"
                    color="primary.textTitleColor"
                  >
                    {dropText}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="bodyLarge500"
                      color="primary.interactiveDefault"
                    >
                      {draggableText}
                    </Typography>
                    <Button onClick={onButtonClick} variant={buttonVariant}>
                      {buttonLabel}
                    </Button>
                  </>
                )}
              </div>
            </label>
          </form>
        )}
        {error && errorMessage && (
          <Typography variant="bodySmall" color="primary.error" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        )}

        {files &&
          renderItem === undefined &&
          files.map((file, fileIndex) => (
            <Stack
              key={fileIndex}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Stack direction="column">
                <Typography variant="boldLabel">{file.name}</Typography>
                <Typography variant="body" color="primary.textSubduedColor">
                  {Math.floor(file.size / 1000)} KB
                </Typography>
              </Stack>
              <IconButton
                size="medium"
                color="error"
                onClick={() => {
                  onRemove(fileIndex);
                }}
              >
                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          ))}
        {renderItem &&
          files &&
          files.map((file, fileIndex) => (
            <React.Fragment key={fileIndex}>
              {renderItem(file, fileIndex)}
            </React.Fragment>
          ))}
      </Stack>
    );
  },
  {
    name: "VaporUploader",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme }) => ({}));
